import {
	Box,
	Card,
	CloseButton,
	Link,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Text
} from "@chakra-ui/react";
import LocalSpinner from "components/LocalSpinner";
import { useQuery } from "react-query";
import { getWeaviateQuestion, getWeaviateSlice } from "api/ai/ai.service";
import questionService from "api/issb/question.service";
import { useRightSidebar } from "context/RightSidebarContext";
import { RightSidebarHeader } from "components/Layout/RightSidebarWidget";
import { H3 } from "components/text/headings";
import { QCardBody, QCardHeader } from "./IssbDataInput/components/QuestionCard";

/**
 * Shared citation content used by both the right sidebar panel and the modal.
 */
function CitationContent({ citation, domElement, questionId, label, title }) {
  let dataUrl = citation && citation["data-url"];
  if ( ! label) label = domElement?.textContent;

  let weaviateQuestionId = citation && citation["data-question_id"];
  let weaviateSourceId = citation && citation["data-source_id"];

  const { data: question, isLoading: isLoadingQuestion } = useQuery({
    queryKey: ["question", weaviateQuestionId || questionId],
    queryFn: async () => {
		if ( ! questionId) {
		const wq = await getWeaviateQuestion(weaviateQuestionId);
		questionId = wq.questionId;
		}
      return questionService.get(questionId);
    },
    enabled: !dataUrl && !!(weaviateQuestionId || questionId),
  });

  const { data: slice, isLoading: isLoadingSlice } = useQuery({
    queryKey: ["slice", weaviateSourceId],
    queryFn: () => getWeaviateSlice(weaviateSourceId),
    enabled: !dataUrl && !weaviateQuestionId && !questionId && !!weaviateSourceId,
  });

  // URL citations (web search results)
  if (dataUrl) {
    if ( ! title) title = citation?.title;
    return (
      <Card p={5} mt={4}>
        <H3>{title || "Web Source"}</H3>
        <Link href={dataUrl} isExternal color="blue.500" fontSize="sm" mt={2} wordBreak="break-all">{dataUrl}</Link>
      </Card>
    );
  }

  // File/source citations (data-source_id = Weaviate slice UUID)
  if (weaviateSourceId && !weaviateQuestionId && !questionId) {
    if ( ! title) title = citation?.title || slice?.title;
    const page = citation?.["data-page"] || slice?.page;
    return (
      <>
        {isLoadingSlice && <LocalSpinner />}
        {!isLoadingSlice && !slice && (
          <Card p={5} mt={4}>
            <H3>{title || "Source"}</H3>
            {page && <Text fontSize="sm" color="gray.500">Page {page}</Text>}
            <Text mt={2}>Details unavailable</Text>
          </Card>
        )}
        {slice && (
          <Card p={5} mt={4}>
            <H3>{title || slice.fileName || "Source"}</H3>
            {slice.fileName && title && title !== slice.fileName && (
              <Text fontSize="sm" color="gray.500">{slice.fileName}</Text>
            )}
            {page && <Text fontSize="sm" color="gray.500">Page {page}</Text>}
            {slice.content && (
              <Text mt={3} fontSize="sm" whiteSpace="pre-wrap">{slice.content}</Text>
            )}
          </Card>
        )}
      </>
    );
  }

  // Question citations (data-question_id = Weaviate question UUID)
  if ( ! title) title = citation?.title || question?.title;

  return (
    <>
      <H3 mt={4}>{title}</H3>
      {isLoadingQuestion && <LocalSpinner />}
      {!isLoadingQuestion && !question && (
        <Card p={5}>
          <Text>Details unavailable</Text>
        </Card>
      )}
      {question && (
        <Card p={5}>
          <QCardHeader
            reference={question.issbReference}
            title={question.question}
            stage={question.stage}
            stageType={question.stageType}
            owner={question.owner}
            assignees={question.assignees}
            id={question.id}
          />
          <QCardBody q={question} showMultiPartAnswer={true} readOnly={true} />
        </Card>
      )}
    </>
  );
}

/**
 * Citation panel for the right sidebar (used by ReportDocEditorPanel).
 */
export default function CitationSidePanel({ citation, domElement, questionId, label, title, reportDoc }) {
  const { doClose } = useRightSidebar();
  if ( ! label) label = domElement?.textContent;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <RightSidebarHeader>Citation {label}</RightSidebarHeader>
        <CloseButton onClick={doClose} />
      </Box>
      <CitationContent citation={citation} domElement={domElement} questionId={questionId} label={label} title={title} />
    </Box>
  );
}

/**
 * Citation modal for use in AI chat (where the right sidebar is not available).
 */
export function CitationModal({ isOpen, onClose, citation, domElement }) {
  const label = domElement?.textContent;
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Citation {label}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <CitationContent citation={citation} domElement={domElement} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
